/* eslint-disable no-nested-ternary */
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Ps, PsTreeItem, PsTreeSearchConditions } from '../../../../api/model';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import PsDetails from './PsDetails';
import PsDetailsPageToolbar from './PsDetailsPageToolbar';
import PsDiffGetter from './PsDiffGetter';
import PsGetter from './PsGetter';
import PsTreeDisplayOptionsDialog from './PsTreeDisplayOptionsDialog';
import { PsDetailsMode } from './constants/types';
import { psTreeSearchConditionsSchema } from './constants/variables';
import PsDiffTreeGetter from './tree/PsDiffTreeGetter';
import PsTreeGetter from './tree/PsTreeGetter';

/** PS 詳細系 (閲覧・追加・編集・比較) 画面。 */
function PsDetailsPage() {
  // i18n 用。
  const { t, i18n } = useTranslation();

  // const defaultOptions = useDefaultOptions(i18n.language);

  const location = useLocation();
  const navigate = useNavigate();

  // console.log('location: ', location);

  // path からモードを判別する。
  let mode: PsDetailsMode = 'View';
  if (location.pathname.includes('add')) {
    mode = 'Add';
  } else if (location.pathname.includes('edit')) {
    mode = 'Edit';
  } else if (location.pathname.includes('difftree')) {
    mode = 'DiffTree';
  } else if (location.pathname.includes('diff')) {
    mode = 'Diff';
  }

  // /** 追加・編集状態。 */
  // const isAddingOrEditing = ((mode === 'Add') || (mode === 'Edit'));

  // React Router の機能でパラメーターを取得。

  // PS バージョン ID: 閲覧・編集時は閲覧・編集対象 PS の、追加時は追加 PS の親となる PS の PS バージョン ID。
  // PS 種別: 1 (章 PS), 2 (タイトル PS), 3 (コンテンツ PS)。
  // 比較元の PS バージョン ID。
  // 比較先の PS バージョン ID。
  const {
    psVersionId: psVersionIdParamString,
    type: typeParamString,
    olderPsVersionId: olderPsVersionIdParamString,
    newerPsVersionId: newerPsVersionIdParamString,
  } = useParams();

  // URL で指定したパラメーターのどれかが不適切であれば true。
  let isInvalidParam = false;

  let olderPsVersionIdParam;
  let newerPsVersionIdParam: unknown;

  if (mode === 'Diff' || mode === 'DiffTree') {
    // 比較では旧バージョンと新バージョンの ID があるはず。

    if (olderPsVersionIdParamString && newerPsVersionIdParamString) {
      olderPsVersionIdParam = parseInt(olderPsVersionIdParamString, 10);
      newerPsVersionIdParam = parseInt(newerPsVersionIdParamString, 10);

      if (Number.isNaN(olderPsVersionIdParam) || Number.isNaN(newerPsVersionIdParam)) {
        isInvalidParam = true;
      }
    } else {
      isInvalidParam = true;
    }
  }

  let typeParam;

  if (mode === 'Add') {
    // 追加では PS 種別があるはず。

    if (typeParamString) {
      typeParam = parseInt(typeParamString, 10);

      // PS 種別は 1 ~ 3(Add) 
      if (Number.isNaN(typeParam) || typeParam < 1 || typeParam > 3) {
        isInvalidParam = true;
      }
    } else {
      isInvalidParam = true;
    }
  }

  /** URL で指定された PS バージョン ID。 */
  let psVersionIdParam: number | undefined;

  if (psVersionIdParamString) {
    psVersionIdParam = parseInt(psVersionIdParamString, 10);

    if (Number.isNaN(psVersionIdParam)) {
      psVersionIdParam = undefined;
      isInvalidParam = true;
    }
  } else if (mode === 'Add' || mode === 'Edit') {
    // 追加・編集では psVersionId があるはず。
    isInvalidParam = true;
  }

  // PS 追加画面: PS コンテンツ変換 API (/pss/content/${psVersionId}) を呼びたいが、改廃案保存前は PS バージョン ID がない。
  // この API に渡される PS バージョン ID はサーバー上の画像の一時保存先のフォルダ名になるだけなので、実在する PS バージョン ID でなくても、数字ならなんでもよい。
  // それで PS 新規追加時には、表示時のミリ秒単位のタイムスタンプなどを入れる。改廃案保存ボタンを押すまでは、CKEditor で保存ボタンを押す度に同じ値を渡す。他になるべく一意になるような、既存の PS バージョン ID と被らないような十分大きな桁の数値であればそれでもよい。
  // ということなので ↓
  /** PS 追加画面にて、新規追加する PS の、改廃案保存するまでの間一時的に PS バージョン ID として扱う値。マウント時の Date.now() の返り値。 */
  const tmpPsVersionIdRef = useRef(Date.now());

  // ダイアログの表示・非表示用 state。

  // PS 閲覧画面。

  /** (PS ツリー) 表示オプション ダイアログ表示・非表示 state。 */
  const [isPsTreeDisplayOptionsDialogOpen, setIsPsTreeDisplayOptionsDialogOpen] = useState<boolean>(false);
  // /** 状態履歴ダイアログ表示・非表示 state。 */
  // const [isPsStateHistoryDialogOpen, setIsPsStateHistoryDialogOpen] = useState<boolean>(false);
  // /** 状態履歴ダイアログに渡すバージョン ID。< 0 でダイアログ非表示、>= 0 でダイアログ表示。 */
  // const [psStateHistoryDialogVersionId, setPsStateHistoryDialogVersionId] = useState<number>(-1);

  /** 差分ツリーフラグ state */
  const [isDiffTree, setIsDiffTree] = useState<boolean>(false);

  // PS 編集画面。

  // /** 法規適用国指定ダイアログ表示・非表示 state。 */
  // const [isPsLegalRegionSelectDialogOpen, setIsPsLegalRegionSelectDialogOpen] = useState<boolean>(false);

  // その他の state。

  // PsDetailsPage で定義された state の psTreeSearchConditions が PsTreeDisplayOptionsDialog で変更され、PsTree に渡され、PsTree で API の postPsTreeSearch を呼ぶという流れ。

  // デフォルト値によって構成される object は Schema.default() もしくは Schema.getDefault() で取得可能。
  // https://www.npmjs.com/package/yup#object-schema-defaults
  // https://www.npmjs.com/package/yup#schemagetdefaultoptions-object-any

  /** API の postPsTreeSearch の検索条件 PsTreeSearchConditions。 */
  const [psTreeSearchConditions, setPsTreeSearchConditions] = useState<PsTreeSearchConditions>(
    psTreeSearchConditionsSchema.getDefault() as PsTreeSearchConditions
  );
  // console.log('psTreeSearchConditions: ', psTreeSearchConditions);

  /** (検索条件が変更されていなくても) 再検索 (postPsTreeSearch) するか否か。 */
  const [doRePostPsTreeSearch, setDoRePostPsTreeSearch] = useState<boolean>(false);

  /** PsDetailsPageToolbar 等で操作の対象となる PS。PsDetails で設定し、PsDetailsPageToolbar 等に渡す。 */
  const [targetPs, setTargetPs] = useState<Ps | undefined>(undefined);

  // console.log('targetPs in PsDetailsPage:', targetPs);

  // /** LegalRegion の配列。法規適用国指定ダイアログで設定し、(PS ツリー) 表示オプション ダイアログなどに渡す。 */
  // const [legalRegions, setLegalRegions] = useState<LegalRegion[]>([]);

  /** PS 出力・原作用 PS 出力用。出力対象となる、現時点での PS ツリーの search_result = true となっている PS の バージョン ID の配列。 */
  const [psVersionIdsToDownload, setPsVersionIdsToDownload] = useState<number[]>([]);

  // console.log('psVersionIdsToDownload: ', psVersionIdsToDownload);

  // PS 閲覧画面:

  // 画面ロード時 (ログイン後、初回の表示の場合):
  // 特定の PS バージョン ID を指定して PS 閲覧画面に遷移した場合は、PS ツリー上の該当する項目を選択した状態とする。
  // そうでない場合は、未選択状態とする。

  // 画面ロード時 (ログイン後、2 回目以降の表示の場合):
  // 特定の PS バージョン ID を指定して PS 閲覧画面に遷移した場合は、PS ツリー上の該当する項目を選択した状態とする。
  // そうでない場合は、前回選択されていた項目を選択状態とする。

  /** 前回選択されていた PS のバージョン ID。 */
  const previouslySelectedPsVersionId = sessionStorageUtils.get('PsDetails.PsVersionId', undefined);

  // タイトル PS を選択した際には、直下のコンテンツ PS があれば、それも共に右ペインの PS 詳細画面に表示する。

  /** PS 閲覧画面で PS ツリーをクリックして選択したか、URL で指定されたか、前回選択された PS のバージョン ID。 */
  const [selectedPsVersionId, setSelectedPsVersionId] = useState<number | undefined>(
    mode !== 'DiffTree' ? psVersionIdParam || previouslySelectedPsVersionId : undefined
  );

  // console.log('selectedPsVersionId', selectedPsVersionId);

  useEffect(() => {
    // 初回だけパラメータから設定(DiffTreeモードの場合)
    if (mode === 'DiffTree') {
      setSelectedPsVersionId(newerPsVersionIdParam as number);
    }
  }, [psVersionIdParam, previouslySelectedPsVersionId, mode, newerPsVersionIdParam]);

  /** selectedPsVersionId が指す PsTreeItem、もしくは targetPsId と PS ID が一致する PsTreeItem。 */
  const [selectedPsTreeItem, setSelectedPsTreeItem] = useState<PsTreeItem | undefined>(undefined);

  // console.log('selectedPsTreeItem', selectedPsTreeItem);

  /** 選択した PS がタイトル PS だった場合の直下のコンテンツ PSs の PsTreeItems。 */
  const [selectedChildContentsPsTreeItems, setSelectedChildContentsPsTreeItems] = useState<PsTreeItem[]>([]);

  /** 選択した PS がタイトル PS だった場合の直下のコンテンツ PSs の PS バージョン IDs。 */
  const selectedChildContentsPsVersionIds = useMemo(
    () =>
      selectedChildContentsPsTreeItems.map(
        (childContentsPsTreeItem: PsTreeItem) => childContentsPsTreeItem.ps_version_id as number
      ),
    [selectedChildContentsPsTreeItems]
  );

  // /** 選択した PS がタイトル PS だった場合の直下のコンテンツ PS の PS ID と PS バージョン ID の配列。 */
  // const [selectedChildContentsPsIdVersionIds, setSelectedChildContentsPsIdVersionIds] = useState<number[][]>([]);

  // console.log('selectedChildContentsPsVersionIds', selectedChildContentsPsVersionIds);

  /** psVersionIdParam を比較用に保存する。 */
  const [previousPsVersionIdParam, setPreviousPsVersionIdParam] = useState<number | undefined>(undefined);

  /** PS出力可能かどうか(PSリストを取得するまでPS出力リンクを無効化) */
  const [canPsDownload, setCanPsDownload] = useState<boolean>(false);

  // useEffect(
  //   () => {
  //     if (psVersionIdParam !== previousPsVersionIdParam) {
  //       setPreviousPsVersionIdParam(psVersionIdParam);
  //     }
  //   },
  //   [previousPsVersionIdParam, psVersionIdParam]
  // );

  // console.log('psVersionIdParam: ', psVersionIdParam);
  // console.log('previousPsVersionIdParam: ', previousPsVersionIdParam);

  // console.log('sessionStorageUtils.get("PsDetails.PsVersionId", undefined): ', previouslySelectedPsVersionId);
  // console.log('selectedPsVersionId: ', selectedPsVersionId);

  const setNewlySelectedPsVersionId = useCallback(
    (
      newlySelectedPsVersionId: number | undefined
      // doUpdateState = true
      // forceUpdate = false
    ) => {
      // console.log('selectedPsVersionId: ', selectedPsVersionId);
      // console.log('newlySelectedPsVersionId: ', newlySelectedPsVersionId);

      // if (
      //   // 強制的に更新。
      //   (forceUpdate)
      //   ||
      //   // 現在の値と異なっていた場合に実行。
      //   (newlySelectedPsVersionId !== selectedPsVersionId)
      // ) {

      // console.log('do update!!');

      // 現在の値と異なっていた場合に実行。
      if (newlySelectedPsVersionId !== selectedPsVersionId) {
        // states 更新。
        setSelectedPsVersionId(newlySelectedPsVersionId);
        setSelectedPsTreeItem(undefined);
        setSelectedChildContentsPsTreeItems([]);
        // console.log('setSelectedPsTreeItem reset');
        // setSelectedChildContentsPsIdVersionIds([]);
        // setSelectedChildContentsPsVersionIds([]);
      }

      if (mode === 'View') {
        // PS 閲覧画面の場合。

        // セッション ストレージに保存。
        if (typeof newlySelectedPsVersionId === 'number') {
          sessionStorageUtils.set('PsDetails.PsVersionId', newlySelectedPsVersionId);
        } else {
          sessionStorage.removeItem('PsDetails.PsVersionId');
        }

        const newPath = `/${i18n.language}/ps/view/${newlySelectedPsVersionId || ''}`;

        if (newPath !== location.pathname) {
          // 現在値と異なっていた場合。
          // URL 更新。
          navigate(newPath, { replace: false });
        }
        // window.history.replaceState(null, '', `/${i18n.language}/ps/view/${(newlySelectedPsVersionId) || ''}`);
      }

      // }
    },
    [i18n.language, location.pathname, mode, navigate, selectedPsVersionId]
  );

  useEffect(() => {
    if (mode !== 'DiffTree') {
      if (typeof psVersionIdParam !== 'number') {
        // console.log('psVersionIdParam !== "number"');

        // URL で PS バージョン ID が指定されていなかった場合。
        // sessionStorage に保存されていた PS を選択する。
        setNewlySelectedPsVersionId(previouslySelectedPsVersionId);
      } else if (psVersionIdParam !== previousPsVersionIdParam) {
        // console.log('psVersionIdParam !== previousPsVersionIdParam');

        // location (URL) が変わった場合。
        // 新規に指定された PS を選択する。
        setNewlySelectedPsVersionId(psVersionIdParam);
      }

      if (psVersionIdParam !== previousPsVersionIdParam) {
        // 今回 URL で指定された PS バージョン ID を比較用に保存しておく。
        setPreviousPsVersionIdParam(psVersionIdParam);
      }
    }
  }, [mode, previousPsVersionIdParam, previouslySelectedPsVersionId, psVersionIdParam, setNewlySelectedPsVersionId]);

  /** PS 選択状態の解除をするか否か。 */
  const [doClearSelectedPsVersionIds, setDoClearSelectedPsVersionIds] = useState<boolean>(false);

  // useEffect(
  //   () => {
  //     // if (typeof psVersionId === 'number') {
  //     // URL 変更時。
  //     setNewlySelectedPsVersionId(psVersionId);
  //     // }
  //   },
  //   [psVersionId, setNewlySelectedPsVersionId]
  // );

  // if ((typeof selectedPsVersionId === 'number') && (mode === 'View')) {
  //   // 画面表示時。
  //   // セッション ストレージと URL 更新。
  //   setNewlySelectedPsVersionId(
  //     selectedPsVersionId
  //     // , false
  //   );
  // }

  // console.log('selectedPsVersionId: ', selectedPsVersionId);
  // console.log('selectedChildContentsPsVersionIds: ', selectedChildContentsPsVersionIds);

  useEffect(() => {
    if (doClearSelectedPsVersionIds) {
      // 選択状態を解除する。
      setNewlySelectedPsVersionId(undefined);
      // setSelectedChildContentsPsVersionIds([]);
      // PS 選択解除実行フラグをリセット。
      setDoClearSelectedPsVersionIds(false);
    }
  }, [doClearSelectedPsVersionIds, setNewlySelectedPsVersionId]);

  // /** 「改廃案保存」ボタンが押されたら true、成功 / 失敗に関わらず、実行したら false に設定される。 */
  // const [doSavePs, setDoSavePs] = useState<boolean>(false);

  // /** PS 追加・編集画面で変更された Pss (type は Schema の型)。 */
  // const [modifiedPss, setModifiedPss] = useState<PsSchemaType[]>([]);

  // useEffect(
  //   () => {
  //     if (
  //       // 「改廃案保存」ボタンが押された。
  //       (doSavePs)
  //       &&
  //       (
  //         (mode === 'Add')
  //         &&
  //         // 追加された PS が modifiedPss に保存された。
  //         (modifiedPss.length === 1)
  //       )
  //       ||
  //       (
  //         (mode === 'Edit')
  //         &&
  //         // 編集された PS が全て modifiedPss に保存された。
  //         (modifiedPss.length === (selectedChildContentsPsVersionIds.length + 1))
  //       )
  //     ) {

  //       // TODO: API 呼び出し。

  //       console.log('modifiedPss: ', modifiedPss);

  //       // doSavePs を false にする。
  //       setDoSavePs(false);
  //       // modifiedPss を空にする。
  //       setModifiedPss([]);
  //     }
  //   },
  //   [doSavePs, modifiedPss, mode, selectedChildContentsPsVersionIds]
  // );

  /** ドラッグ & ドロップで変更されたルートの PsTreeItem。PsTree で変更されたものをこの state 経由で PsDetails に渡す。 */
  const [modifiedRootPsTreeItem, setModifiedRootPsTreeItem] = useState<PsTreeItem | undefined>(undefined);

  // console.log('modifiedRootPsTreeItem:', modifiedRootPsTreeItem);

  /** 一括変更用の表示言語の state。0: 日本語、1: 米語、2: 英語。 */
  const [bulkDisplayLanguages, setBulkDisplayLanguages] = useState<boolean[]>([true, true, true]);

  // /** PS 追加・編集画面 → 他の画面へ遷移時にワーニングを表示するため、編集状態とする。 */
  // const setEditMode = useEditModeSetValue();

  // useEffect(
  //   () => {
  //     setEditMode((!isInvalidParam) && ((mode === 'Add') || (mode === 'Edit')));
  //   },
  //   [isInvalidParam, mode, setEditMode]
  // );

  // t() の引数は static なものにする。
  // https://i18next-extract.netlify.app/#/faq?id=why-does-the-plugin-show-a-warning-when-extracting-my-key
  const title = (
    <title>
      {mode === 'View'
        ? t('CMS-POC|PS閲覧')
        : mode === 'Add'
        ? t('CMS-POC|PS追加')
        : mode === 'Edit'
        ? t('CMS-POC|PS編集')
        : t('CMS-POC|PS比較')}
      {/* {
    // i18next-extract-mark-context-next-line ["PS閲覧", "PS追加", "PS編集", "PS比較"]
    t(`ORIAS|${title}`, { context: title })
  } */}
    </title>
  );

  if (isInvalidParam) {
    // URL が不適切。
    return (
      <>
        <Helmet>{title}</Helmet>
        <div id="main">{t('この URL は存在しません。')}</div>
      </>
    );
  }

  // console.log('isPsStateHistoryDialogOpen: ', isPsStateHistoryDialogOpen);

  return (
    <>
      <Helmet>{title}</Helmet>
      <div id="main">
        <div
          id="product-manage"
          // style={{
          //   position: 'absolute',
          //   overflow: 'hidden !important',
          // }}
        >
          {/* PSツリー。 */}
          {mode === 'Diff' || mode === 'DiffTree' ? (
            // PS 比較画面用 PS ツリー データ取得コンポーネント。内部に PsTree を持つ。
            <PsDiffTreeGetter
              mode={mode}
              olderPsVersionId={olderPsVersionIdParam as number}
              newerPsVersionId={newerPsVersionIdParam as number}
              selectedPsVersionId={selectedPsVersionId}
              selectedPsTreeItem={selectedPsTreeItem}
              targetPs={targetPs}
              selectedChildContentsPsTreeItems={selectedChildContentsPsTreeItems}
              setSelectedPsVersionId={
                (newlySelectedPsVersionId: number) => {
                  setNewlySelectedPsVersionId(newlySelectedPsVersionId);
                }
              }
              setSelectedPsTreeItem={(newlySelectedPsTreeItem: PsTreeItem) => {
                setSelectedPsTreeItem(newlySelectedPsTreeItem);
              }}
              setSelectedChildContentsPsTreeItems={
                // PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時か、prop の selectedPsVersionId が psTreeItem.ps_version_id と等しい時) に呼ばれる関数。
                (newSelectedChildContentsPsTreeItems: PsTreeItem[]) => {
                  // 異なっていた場合のみ state を更新。
                  if (
                    JSON.stringify(selectedChildContentsPsVersionIds) !==
                    JSON.stringify(
                      newSelectedChildContentsPsTreeItems.map(
                        (childContentsPsTreeItem: PsTreeItem) => childContentsPsTreeItem.ps_version_id as number
                      )
                    )
                  ) {
                    setSelectedChildContentsPsTreeItems(newSelectedChildContentsPsTreeItems);
                  }
                }
              }
            />
          ) : (
            // PS 比較画面以外用 PS ツリー データ取得コンポーネント。内部に PsTree を持つ。
            <PsTreeGetter
              mode={mode}
              // PsDetailsPage で定義された state の psTreeSearchConditions が PsTreeDisplayOptionsDialog で変更され、PsTreeGetter に渡され、PsTreeGetter で API の postPsTreeSearch を呼ぶという流れ。
              psTreeSearchConditions={psTreeSearchConditions}
              // 検索条件が変更されていないが、再検索が必要な場合 true。
              doRePostPsTreeSearch={doRePostPsTreeSearch}
              setDoRePostPsTreeSearch={(newDoRePostPsTreeSearch: boolean) => {
                setDoRePostPsTreeSearch(newDoRePostPsTreeSearch);
              }}
              selectedPsVersionId={selectedPsVersionId}
              selectedPsTreeItem={selectedPsTreeItem}
              targetPs={targetPs}
              // targetPsId={targetPs?.ps_id}
              selectedChildContentsPsTreeItems={selectedChildContentsPsTreeItems}
              // selectedChildContentsPsIds={
              //   selectedChildContentsPsIdVersionIds.map(
              //     (psIdVersionId: number[]) => psIdVersionId[0]
              //   )
              // }
              // selectedChildContentsPsTreeItems={selectedChildContentsPsTreeItems}
              // selectedChildContentsPsVersionIds={selectedChildContentsPsVersionIds}
              setSelectedPsVersionId={
                // PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時) に呼ばれる関数。
                (newlySelectedPsVersionId: number) => {
                  // 異なっていた場合のみ state を更新。
                  // If the new value you provide is identical to the current state, as determined by an Object.is comparison, React will skip re-rendering the component and its children.
                  // なので、単一の値の場合はこちらで比較する必要はない。
                  // if (selectedPsVersionId !== selectedPsVersionIdInPsTree) {
                  setNewlySelectedPsVersionId(newlySelectedPsVersionId);
                  // }
                }
              }
              setSelectedPsTreeItem={(newlySelectedPsTreeItem: PsTreeItem) => {
                // if (!(_.isEqual(selectedPsTreeItem, newlySelectedPsTreeItem))) {
                // console.log('setSelectedPsTreeItem new');

                setSelectedPsTreeItem(newlySelectedPsTreeItem);
                // }
              }}
              setSelectedChildContentsPsTreeItems={
                // PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時か、prop の selectedPsVersionId が psTreeItem.ps_version_id と等しい時) に呼ばれる関数。
                (newSelectedChildContentsPsTreeItems: PsTreeItem[]) => {
                  // 異なっていた場合のみ state を更新。
                  if (
                    JSON.stringify(selectedChildContentsPsVersionIds) !==
                    JSON.stringify(
                      newSelectedChildContentsPsTreeItems.map(
                        (childContentsPsTreeItem: PsTreeItem) => childContentsPsTreeItem.ps_version_id as number
                      )
                    )
                  ) {
                    setSelectedChildContentsPsTreeItems(newSelectedChildContentsPsTreeItems);
                  }
                }
              }
              setPsVersionIdsToDownload={(newPsVersionIdsToDownload: number[]) => {
                setPsVersionIdsToDownload(newPsVersionIdsToDownload);
              }}
              setModifiedRootPsTreeItem={(newModifiedRootPsTreeItem: PsTreeItem) => {
                setModifiedRootPsTreeItem(newModifiedRootPsTreeItem);
              }}
              setCanPsDownload={setCanPsDownload}
            />
          )}

          <div
            id="product-manage-main"
            style={
              {
                // position: 'absolute',
                // maxWidth: '100%',
              }
            }
          >
            <div
              style={
                {
                  // position: 'absolute',
                  // maxWidth: '100%',
                }
              }
            >
              {
                // 比較画面以外の場合。
                mode !== 'Diff' && (
                  // 上部のボタン類。
                  <PsDetailsPageToolbar
                    mode={mode}
                    // psVersionId={selectedPsVersionId}
                    targetPs={targetPs}
                    psVersionIdParam={psVersionIdParam}
                    psVersionIdsToDownload={psVersionIdsToDownload}
                    setIsPsTreeDisplayOptionsDialogOpen={(newIsOpen: boolean) => {
                      setIsPsTreeDisplayOptionsDialogOpen(newIsOpen);
                    }}
                    // doRePostPsTreeSearch={doRePostPsTreeSearch}
                    setDoRePostPsTreeSearch={(newDoRePostPsTreeSearch: boolean) => {
                      setDoRePostPsTreeSearch(newDoRePostPsTreeSearch);
                    }}
                    setDoClearSelectedPsVersionIds={(newDoClearSelectedPsVersionIds: boolean) => {
                      setDoClearSelectedPsVersionIds(newDoClearSelectedPsVersionIds);
                    }}
                    // setDoSavePs={
                    //   (newDoSavePs: boolean) => {
                    //     setDoSavePs(newDoSavePs);
                    //   }
                    // }
                    bulkDisplayLanguages={bulkDisplayLanguages}
                    setBulkDisplayLanguages={setBulkDisplayLanguages}
                    canPsDownload={canPsDownload}
                  />
                )
              }

              <div
                style={{
                  overflowY: 'auto',
                  overflowX: 'clip',
                  // TODO?: 計算でヘッダーとツールバーの高さを求める？もしくはデザイナーさんに PS DB と同じ構造にしてもらうか。
                  maxHeight: '85vh',
                  // position: 'absolute',
                  // top: '120px',
                  // left: '400px',
                  scrollBehavior: 'smooth',
                  scrollPaddingTop: '30px',
                }}
              >
                {
                  // PS 閲覧画面・編集画面の場合。
                  (mode === 'View' || mode === 'Edit' || mode === 'DiffTree') &&
                    typeof selectedPsVersionId === 'number' &&
                    // 選択された PS と、選択されたタイトル PS 直下のコンテンツ PSs。
                    [
                      selectedPsVersionId,
                      ...selectedChildContentsPsVersionIds,
                      // ...(
                      //   selectedChildContentsPsIdVersionIds.map(
                      //     (psIdVersionId: number[]) => psIdVersionId[1]
                      //   )
                      // ),
                    ].map((eachSelectedPsVersionId: number) => (
                      <PsGetter
                        key={eachSelectedPsVersionId}
                        mode={mode}
                        // psInfoTabMode
                        // setSelectedDerivationId={
                        //   (selectedDerivationId: number | undefined) => {
                        //     console.log('selectedDerivationId:', selectedDerivationId);
                        //   }
                        // }
                        psVersionId={eachSelectedPsVersionId}
                        isTargetPs={selectedPsVersionId === eachSelectedPsVersionId}
                        setTargetPs={(newTargetPs: Ps) => {
                          // 異なっていた場合のみ state を更新。
                          if (!_.isEqual(targetPs, newTargetPs)) {
                            setTargetPs(newTargetPs);
                          }
                        }}
                        setDoRePostPsTreeSearch={(newDoRePostPsTreeSearch: boolean) => {
                          setDoRePostPsTreeSearch(newDoRePostPsTreeSearch);
                        }}
                        modifiedRootPsTreeItem={modifiedRootPsTreeItem}
                        bulkDisplayLanguages={bulkDisplayLanguages}
                      />
                    ))
                }
                {
                  // PS 追加画面の場合。
                  mode === 'Add' && (
                    // PS を取得 (PsGetter を使用) せず、新規の PS を渡して直接 PsDetails を 1 つだけレンダー。
                    <PsDetails
                      mode={mode}
                      ps={{
                        // 追加時は追加 PS の親となる PS のバージョン ID が URL として渡されている。
                        parent_ps_version_id: selectedPsVersionId,
                        // PS 種別は URL として渡されている。
                        type: typeParam,

                        // 一時的な PS バージョン ID として扱う、マウント時のミリ秒 (Date.now() の返り値)。
                        ps_version_id: tmpPsVersionIdRef.current,
                      }}
                      isTargetPs
                      setTargetPs={(newTargetPs: Ps) => {
                        // 異なっていた場合のみ state を更新。
                        if (!_.isEqual(targetPs, newTargetPs)) {
                          setTargetPs(newTargetPs);
                        }
                      }}
                      setDoRePostPsTreeSearch={(newDoRePostPsTreeSearch: boolean) => {
                        setDoRePostPsTreeSearch(newDoRePostPsTreeSearch);
                      }}
                      modifiedRootPsTreeItem={modifiedRootPsTreeItem}
                    />
                  )
                }
                {
                  // PS 比較画面の場合。
                  mode === 'Diff' && (
                    // 比較用に PS を 2 つ取得し、PsDetails で表示する。
                    <PsDiffGetter
                      mode={mode}
                      olderPsVersionId={olderPsVersionIdParam as number}
                      newerPsVersionId={newerPsVersionIdParam as number}
                      isTargetPs
                      setTargetPs={(newTargetPs: Ps) => {
                        // 異なっていた場合のみ state を更新。
                        if (!_.isEqual(targetPs, newTargetPs)) {
                          setTargetPs(newTargetPs);
                        }
                      }}
                    />
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* (PS ツリー) 表示オプション ダイアログ。 */}
      <PsTreeDisplayOptionsDialog
        isOpen={isPsTreeDisplayOptionsDialogOpen}
        setIsOpen={(newIsOpen: boolean) => {
          setIsPsTreeDisplayOptionsDialogOpen(newIsOpen);
        }}
        setPsTreeSearchConditions={(newPsTreeSearchConditions: PsTreeSearchConditions) => {
          setPsTreeSearchConditions(newPsTreeSearchConditions);
        }}
        // setIsPsLegalRegionSelectDialogOpen={
        //   (newIsOpen: boolean) => {
        //     setIsPsLegalRegionSelectDialogOpen(newIsOpen);
        //   }
        // }
        // legalRegions={legalRegions}
      />
    </>
  );
}

export default PsDetailsPage;
